import { Grid } from '@mui/material';
import { Form, Formik } from 'formik';
import { transformResponseSaveDataAnpr } from 'shell/utils/save-data-anpr-response-transformer';
import { SaveDataAnpr } from 'shell/view-models/save-data-anpr';
import { Fragment, memo, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { __PERSON_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { HttpResponse } from 'shell/models/http-response';
import { ResponseAnprSaveData } from 'shell/models/person';
import { ItemVM } from 'shell/view-models/item';
import { SubscriptionBirthNonResidentCrumbs } from 'subscription/constants/breadcrumb';
import { SubscriptionBirthNonResidentFormVM } from 'shell/components/shared/research-notifications/reasearch-birth-non-resident/subscription-birth-non-resident';
import validationSchemaNonResident, {
  validationSchemaNonResidentFromAct,
} from './validation-schema';
import PmiTitle from 'shell/components/shared/title';
import SubscriptionButtons from 'subscription/components/shared/buttons';
import SubscriptionBirthNonResident from 'subscription/components/birth-non-resident';
import { transformerRequestSubscriptionNonResident } from 'subscription/utils/transformer-request-birth-non-resident';
import { ActType } from 'shell/constants/act-type';
import { emptyDigital, emptyPaper } from 'shell/components/shared/act/constants';

const defaultValues: SubscriptionBirthNonResidentFormVM = {
  birthdayDocument: {
    actType: ActType.AttoCartaceo,
    paper: emptyPaper,
    digital: emptyDigital,
  },
  citizenship: {
    validity: null,
    state: null,
  },
  fatherhood: {},
  motherhood: {},
  generality: {
    surname: '',
    name: '',
    birthDate: null,
    fiscalCode: '',
    italian: {
      municipality: {
        id: '015146',
        description: 'MILANO',
        province: 'MI',
      },
    },
    foreign: {},
    gender: '',
    aire: 'N',
  },
};

interface SubscriptionBirthNonResidentPageProps {
  isWithoutBreadcrumbs?: boolean;
  disabledFromAct?: boolean;
  goBackUrl?: string;
  initialValues?: SubscriptionBirthNonResidentFormVM;
  onConfirm?: () => void;
  onHandleExit?: () => void;
}

const SubscriptionBirthNonResidentPageComponent = ({
  isWithoutBreadcrumbs,
  goBackUrl = '/iscrizioni',
  initialValues = {},
  onConfirm,
  onHandleExit,
  disabledFromAct,
}: SubscriptionBirthNonResidentPageProps): React.ReactElement => {
  const navigate = useNavigate();

  const [responseRegisterBirth, registerBirth] = useHttpFetch<
    HttpResponse<ResponseAnprSaveData>,
    SaveDataAnpr
  >(`${__PERSON_API_URL__}/non-residenti/iscrizione-nascita`, {
    options: {
      method: 'POST',
    },
    transformer: transformResponseSaveDataAnpr,
  });

  const castedSavedData = responseRegisterBirth.data as SaveDataAnpr;

  // visibility
  const [visibility, setVisibility] = useState<{ error?: boolean; warning?: boolean }>({
    error: undefined,
    warning: undefined,
  });

  // on change visibility
  const onVisibility = useCallback(
    (open, type) => {
      const visibilityMessage = type === 'error' && !open ? false : open;

      setVisibility((oldState) => ({ ...oldState, [type]: visibilityMessage }));
    },
    [setVisibility]
  );

  const handleSubmit = useCallback(
    (values: SubscriptionBirthNonResidentFormVM) => {
      const data = transformerRequestSubscriptionNonResident(values, true);
      (
        registerBirth({
          data,
        }) as ResponsePromise<HttpResponse<ResponseAnprSaveData>, SaveDataAnpr>
      ).then((response) => {
        const castedResponse = response as SaveDataAnpr;
        setVisibility({ error: undefined, warning: undefined });

        if (castedResponse?.errors?.length) {
          window.scrollTo(0, 0);
          return;
        }

        if (onConfirm) {
          onConfirm();
          return;
        }

        toast.success('Operazione eseguita con successo');
        navigate(goBackUrl);
      });
    },
    [navigate, registerBirth, setVisibility]
  );

  //Errors
  const errorMessage = useMemo(() => {
    const errorToVisualize: { visibility?: boolean; title?: string; messages?: Array<ItemVM> } = {
      visibility: false,
      title: '',
    };

    if (castedSavedData?.errors?.length && visibility.error === undefined) {
      errorToVisualize.title =
        castedSavedData?.errors?.length > 1
          ? `Ci sono ${castedSavedData?.errors?.length} errori`
          : `C'è 1 errore`;
      errorToVisualize.messages = castedSavedData?.errors;
      errorToVisualize.visibility = true;
      return errorToVisualize;
    }

    errorToVisualize.visibility = visibility.error;

    return errorToVisualize;
  }, [visibility.error, castedSavedData]);

  const handleExit = useCallback(() => {
    if (onHandleExit) {
      onHandleExit();
      return;
    }

    navigate(goBackUrl);
  }, [navigate]);

  return (
    <Fragment>
      {isWithoutBreadcrumbs ? undefined : (
        <Breadcrumbs items={SubscriptionBirthNonResidentCrumbs} />
      )}
      <div>
        <PmiTitle title="Iscrizione Nascita Non Residente" />
      </div>
      <SearchMessages
        type={MessageType.Error}
        title={errorMessage.title || ''}
        messages={errorMessage.messages || []}
        visibility={errorMessage.visibility || false}
        onChangeVisibility={onVisibility}
      />
      <SearchMessages
        type={MessageType.Warning}
        title={`Ci sono ${castedSavedData?.warnings?.length || ''} warning`}
        messages={castedSavedData?.warnings || []}
        visibility={
          visibility.warning !== undefined
            ? visibility.warning
            : (castedSavedData?.warnings || [])?.length > 0
        }
        onChangeVisibility={onVisibility}
      />
      <Formik
        initialValues={{
          ...(defaultValues || {}),
          ...(initialValues || {}),
          generality: initialValues.generality
            ? {
                ...defaultValues.generality,
                ...initialValues.generality,
              }
            : undefined,
          citizenship: initialValues.citizenship
            ? {
                ...defaultValues.citizenship,
                ...initialValues.citizenship,
              }
            : undefined,
        }}
        onSubmit={handleSubmit}
        validationSchema={
          disabledFromAct ? validationSchemaNonResidentFromAct : validationSchemaNonResident
        }
      >
        <Form>
          <Grid container spacing={1}>
            <SubscriptionButtons handleExit={handleExit} />
            <Grid item xs={12} md={10}>
              <SubscriptionBirthNonResident
                isAire
                nonResident
                disabled={disabledFromAct}
                isNotDisabledCitizenship
              />
            </Grid>
          </Grid>
        </Form>
      </Formik>
    </Fragment>
  );
};

SubscriptionBirthNonResidentPageComponent.displayName =
  'Subscription Birth non resident page component';

const SubscriptionBirthNonResidentPage = memo(SubscriptionBirthNonResidentPageComponent);

export default SubscriptionBirthNonResidentPage;
